/**
 * The price list — served by the API (`GET /platform/plans`) so this screen and
 * the billing logic can never disagree. Read-only: changing what a *particular*
 * agency pays happens on their detail page.
 */
import { Check, Info, Star } from '@phosphor-icons/react';
import { usePlans } from '../queries/platform';
import { apiError } from '../lib/api';
import { inr } from '../lib/format';
import { ErrorState, LoadingBlock } from '../components/ui';

export default function Plans() {
  const { data, isPending, isError, error, refetch } = usePlans();

  if (isPending) return <LoadingBlock label="Loading the price list…" />;
  if (isError) return <ErrorState message={apiError(error)} onRetry={() => void refetch()} />;

  const { pricing, addOns, coreFeatures } = data;
  const annualSaving = pricing.monthly * 12 - pricing.annual;

  return (
    <div className="stack animate-up" style={{ gap: 18 }}>
      <div className="grid grid-3" style={{ gap: 18 }}>
        {/* ---- Trial ---- */}
        <div className="card card-pad">
          <div className="row" style={{ marginBottom: 6 }}>
            <h3 style={{ fontSize: 16 }}>Free trial</h3>
            <div className="spacer" />
            <span className="badge tone-info">{pricing.trialDays} days</span>
          </div>
          <p className="num" style={{ margin: '10px 0 0', fontSize: 26, lineHeight: 1 }}>
            Free
          </p>
          <p className="t-sm muted" style={{ margin: '10px 0 0', lineHeight: 1.6 }}>
            Starts the moment an agency is onboarded, and includes{' '}
            <strong style={{ color: 'var(--ink)' }}>every add-on</strong>. An agency that has
            never seen Matches or Wishes has no reason to buy them.
          </p>
          <p className="t-xs faint" style={{ margin: '12px 0 0', lineHeight: 1.6 }}>
            When it ends they can still sign in, but the data screens close until a payment is
            recorded.
          </p>
        </div>

        {/* ---- Monthly ---- */}
        <div className="card card-pad">
          <h3 style={{ fontSize: 16 }}>Base plan, monthly</h3>
          <div style={{ margin: '10px 0 0' }}>
            <span className="num" style={{ fontSize: 26 }}>
              {inr(pricing.monthly)}
            </span>
            <span className="t-sm muted"> /month</span>
          </div>
          <ul style={{ margin: '14px 0 0', padding: 0, listStyle: 'none' }}>
            {coreFeatures.map((feature) => (
              <li
                key={feature}
                style={{ display: 'flex', gap: 9, alignItems: 'flex-start', marginBottom: 9 }}
              >
                <Check size={14} weight="bold" color="var(--ok)" style={{ marginTop: 3, flex: 'none' }} />
                <span className="t-sm" style={{ lineHeight: 1.5 }}>
                  {feature}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* ---- Annual ---- */}
        <div
          className="card card-pad"
          style={{ borderColor: 'var(--ac-tint-2)', boxShadow: 'var(--shadow-md)' }}
        >
          <div className="row" style={{ marginBottom: 6 }}>
            <h3 style={{ fontSize: 16 }}>Base plan, annual</h3>
            <div className="spacer" />
            <span className="badge tone-ok">
              <Star size={11} weight="fill" /> Save {inr(annualSaving)}
            </span>
          </div>
          <div style={{ margin: '10px 0 0' }}>
            <span className="num" style={{ fontSize: 26 }}>
              {inr(pricing.annual)}
            </span>
            <span className="t-sm muted"> /year</span>
          </div>
          <p className="t-sm muted" style={{ margin: '10px 0 0', lineHeight: 1.6 }}>
            Ten months' price for twelve, at {inr(Math.round(pricing.annual / 12))} a month
            equivalent. Everything in the monthly plan.
          </p>
          <p className="t-xs faint" style={{ margin: '12px 0 0', lineHeight: 1.6 }}>
            Add-ons get the same two months free — {inr(pricing.addOnAnnual)} a year each rather
            than {inr(pricing.addOnMonthly * 12)}.
          </p>
        </div>
      </div>

      {/* ---- Add-ons ---- */}
      <div className="card">
        <div className="card-head">
          <div>
            <h2 className="card-title">Add-on features</h2>
            <p className="card-sub">
              {inr(pricing.addOnMonthly)} per feature per month, or {inr(pricing.addOnAnnual)} a
              year, on top of the base plan
            </p>
          </div>
          <div className="spacer" />
          <span className="badge">
            All {addOns.length} · {inr(pricing.monthly + addOns.length * pricing.addOnMonthly)}/mo
          </span>
        </div>

        <div>
          {addOns.map((feature) => (
            <div
              key={feature.key}
              className="table-row"
              style={{ gridTemplateColumns: '1.4fr 2fr auto auto' }}
            >
              <span className="t-md strong">{feature.name}</span>
              <span className="t-sm muted" style={{ lineHeight: 1.5 }}>
                {feature.description}
              </span>
              <span
                className="t-xs faint"
                title={
                  feature.enforcedByApi
                    ? 'The API refuses this when it is not on the plan.'
                    : 'Built in the browser from records the agency already owns, so the API has nothing to withhold. This one is gated in the UI only.'
                }
                style={{ display: 'flex', alignItems: 'center', gap: 5 }}
              >
                {feature.enforcedByApi ? 'API-enforced' : (
                  <>
                    <Info size={12} /> UI-gated
                  </>
                )}
              </span>
              <span style={{ textAlign: 'right' }}>
                <span className="num" style={{ fontSize: 14 }}>
                  {inr(pricing.addOnMonthly)}
                </span>
                <span className="t-xs faint" style={{ display: 'block', marginTop: 2 }}>
                  {inr(pricing.addOnAnnual)}/yr
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="preview-note" style={{ marginBottom: 0 }}>
        <span>
          Prices come from the API, which is also what bills against them. There is no second
          copy in this console. To change the list, edit <code>config/plans.ts</code> in the API.
          Existing subscriptions keep the amount they were sold at.
        </span>
      </div>
    </div>
  );
}
